// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * What sits on top of what.
 *
 * Shapes are drawn in the order they are stored, so the last one drawn is the
 * one you can see and click. Two overlapping areas — a laydown zone over a
 * parking bay — are decided entirely by which was drawn second, and until now
 * the only way to change that was to delete one and draw it again.
 *
 * Order is kept as position in the list rather than as a z number on each
 * element: a number has to be kept unique and renumbered when it runs out of
 * room between neighbours, and the list already is the order.
 */

export interface Identified { id: string }

const indexOf = <T extends Identified>(list: T[], id: string) =>
  (list || []).findIndex((e) => e.id === id);

/** The item moved to a new position, with everything else closing up. */
function moveTo<T extends Identified>(list: T[], from: number, to: number): T[] {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(to, next.length)), 0, moved);
  return next;
}

/** Drawn last, so it covers everything else of its kind. */
export function bringToFront<T extends Identified>(list: T[], id: string): T[] {
  const at = indexOf(list, id);
  if (at < 0 || at === list.length - 1) return list;
  return moveTo(list, at, list.length - 1);
}

/** Drawn first, so everything else of its kind covers it. */
export function sendToBack<T extends Identified>(list: T[], id: string): T[] {
  const at = indexOf(list, id);
  if (at <= 0) return list;
  return moveTo(list, at, 0);
}

/** Past the one immediately above it. */
export function bringForward<T extends Identified>(list: T[], id: string): T[] {
  const at = indexOf(list, id);
  if (at < 0 || at === list.length - 1) return list;
  return moveTo(list, at, at + 1);
}

/** Behind the one immediately below it. */
export function sendBackward<T extends Identified>(list: T[], id: string): T[] {
  const at = indexOf(list, id);
  if (at <= 0) return list;
  return moveTo(list, at, at - 1);
}

/** Whether there is anything above this one to go past. */
export function canRaise<T extends Identified>(list: T[], id: string): boolean {
  const at = indexOf(list, id);
  return at >= 0 && at < (list?.length ?? 0) - 1;
}

/** Whether there is anything below this one to go behind. */
export function canLower<T extends Identified>(list: T[], id: string): boolean {
  return indexOf(list, id) > 0;
}
